import { setup, and, not, enqueueActions, fromPromise, sendTo } from 'xstate';

import { apiCreateOrValidateUser, apiCheckPermissions, ACTIONS } from '.';

export const AUTH_STATES = {
  INIT: 'init',
  INITIAL: 'initial',
  CHECKING_PERMISSIONS: 'checkingPermissions',
  VERIFYING_OR_CREATING_USER: 'verifyingOrCreatingUser',
  NEEDS_VERIFICATION: 'needsVerification',
  NEEDS_APPROVAL: 'needsApproval',
  DONE: 'done',
};

export const AUTH_ACTIONS = {
  SUBMIT_EMAIL: 'submitEmail',
  CLEAR_EMAIL: 'clearEmail',
};

const LS_KEY = 'emailEntered';

const initialContext = (input) => ({
  api: input.api,
  userId: input.userId,
  userEmail: input.userEmail || localStorage.getItem(LS_KEY),
  parentRef: input.parentRef,
  editorId: input.editorId,
  redirect: input.redirect,
  onImmediateAuthSuccess: input.onImmediateAuthSuccess,
});

export const authMachine = setup({
  actions: {
    saveEmail: enqueueActions(({ enqueue, event }) => {
      enqueue(() => {
        localStorage.setItem(LS_KEY, event.userEmail);
      });
      enqueue.assign({
        userEmail: event.userEmail,
      });
    }),
    clearEmail: enqueueActions(({ enqueue }) => {
      enqueue(() => {
        localStorage.removeItem(LS_KEY);
      });
      enqueue.assign({
        userEmail: null,
      });
    }),
    sendErrorToParent: enqueueActions(({ enqueue, context, event }) => {
      enqueue.sendTo(context.parentRef, {
        type: ACTIONS.REPORT_ERROR,
        title: event.title,
        error: event.error,
      });
      enqueue.sendTo(
        context.parentRef,
        {
          type: ACTIONS.BACK,
        },
        {
          delay: 100,
        },
      );
    }),
    sendMetric: sendTo(
      ({ context }) => context.parentRef,
      (_, params) => ({
        type: ACTIONS.TRACK_METRIC,
        params: {
          name: params.name,
          params: params.params,
        },
      }),
    ),
  },
  actors: {
    apiCreateOrValidateUser: fromPromise(apiCreateOrValidateUser),
    apiCheckPermissions: fromPromise(apiCheckPermissions),
  },
  guards: {
    isLoggedIn: ({ context }) => Boolean(context.userId),
    enteredEmail: ({ context }) => Boolean(context.userEmail),
  },
}).createMachine({
  output: ({ context }) => ({
    onImmediateAuthSuccess: context.onImmediateAuthSuccess,
  }),
  context: ({ input }) => initialContext(input),
  initial: AUTH_STATES.INIT,
  states: {
    [AUTH_STATES.INIT]: {
      always: [
        {
          guard: and([not('isLoggedIn'), not('enteredEmail')]),
          target: AUTH_STATES.INITIAL,
        },
        {
          guard: and([not('isLoggedIn'), 'enteredEmail']),
          actions: [{ type: 'sendMetric', params: { name: 'email_entered' } }],
          target: AUTH_STATES.VERIFYING_OR_CREATING_USER,
        },
        {
          actions: [{ type: 'sendMetric', params: { name: 'email_entered' } }],
          target: AUTH_STATES.CHECKING_PERMISSIONS,
        },
      ],
    },
    [AUTH_STATES.INITIAL]: {
      on: {
        [AUTH_ACTIONS.SUBMIT_EMAIL]: {
          actions: ['saveEmail'],
          target: AUTH_STATES.INIT,
        },
      },
    },
    [AUTH_STATES.CHECKING_PERMISSIONS]: {
      invoke: {
        src: 'apiCheckPermissions',
        input: ({ context }) => {
          return { api: context.api, redirect: context.redirect };
        },
        onDone: [
          {
            guard: ({ event }) => event.output.sentOwnerEmail,
            target: AUTH_STATES.NEEDS_APPROVAL,
          },
          {
            target: AUTH_STATES.DONE,
          },
        ],
        onError: {
          actions: [
            {
              type: 'sendErrorToParent',
              params: ({ event }) => ({
                title: 'Failed to verify account.',
                error: event.error,
              }),
            },
          ],
        },
      },
    },
    [AUTH_STATES.VERIFYING_OR_CREATING_USER]: {
      invoke: {
        src: 'apiCreateOrValidateUser',
        input: ({ context }) => {
          return {
            api: context.api,
            userEmail: context.userEmail,
            editorId: context.editorId,
            redirect: context.redirect,
          };
        },
        onDone: [
          {
            guard: ({ event }) => event.output.sentOwnerEmail,
            target: AUTH_STATES.NEEDS_APPROVAL,
          },
          {
            guard: ({ event }) => event.output.sentUserValidationEmail,
            target: AUTH_STATES.NEEDS_VERIFICATION,
          },
          {
            actions: [
              sendTo(
                ({ context }) => context.parentRef,
                ({ event }) => ({
                  type: ACTIONS.SET_NEW_USER,
                  output: {
                    userId: event.output.userId,
                    userEmail: event.output.userEmail,
                    hasPublishPermissions: event.output.publishAccess,
                    capabilities: event.output.capabilities,
                  },
                }),
              ),
            ],
            target: AUTH_STATES.DONE,
          },
        ],
        onError: {
          actions: [
            {
              type: 'sendErrorToParent',
              params: ({ event }) => ({
                title: 'Failed to verify account.',
                error: event.error,
              }),
            },
          ],
        },
      },
    },
    [AUTH_STATES.NEEDS_VERIFICATION]: {
      on: {
        [AUTH_ACTIONS.CLEAR_EMAIL]: {
          actions: ['clearEmail'],
          target: AUTH_STATES.INITIAL,
        },
      },
    },
    [AUTH_STATES.NEEDS_APPROVAL]: {},
    [AUTH_STATES.DONE]: {
      entry: [{ type: 'sendMetric', params: { name: 'email_verified' } }],
      type: 'final',
    },
  },
});
